import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PearlHarbor} from "../PearlHarbor";
import {gsap, TimelineLite} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {PearlHarborGameAssets} from "../PearlHarborGameAssets";
import {PearlHarborSoundManager} from "../sound/PearlHarborSoundManager";
import {PearlHarborSoundConfig} from "../sound/PearlHarborSoundConfig";

export class PearlHarborWinCapView extends PIXI.Container implements StageLayer {

    private _overlay: PIXI.Graphics;
    private _overlayColor: number = 0x000000;
    private _spine: PIXI.spine.Spine;
    private _mc:PIXI.Sprite;

    private _button: ContinueButton;
    private _buttonCallBack: () => void;

    private _countUp: BasicBitmapCountUp;
    private _trackCount: number = 0;

    constructor() {
        super();
        this.name = "WIN_CAP";
        this.init();
    }

    private init(): void {
        this.createOverlay();
        this.createGraphics();
        this.createCountUp();
        this.createContinueBtn();
        this.visible=false;
    }

    private createOverlay(): void {
        this._overlay = new PIXI.Graphics();
        this._overlay.alpha = 0;
        this.addChild(this._overlay);
    }

    private createGraphics(): void {
        const spineObj: PIXI.spine.Spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("capWin"));
        spineObj.position.set(360, 360);
        spineObj.visible = false;
        this._spine = spineObj;
        this.addChild(spineObj);

        this._mc = new PIXI.Sprite(PearlHarbor.assetLoader.getTextures(`bigwins/cw/missionComplete`)[0]);
        this._mc.anchor.set(0.5);
        this._mc.position.set(360, 106);
        this._mc.visible = false;
        this.addChild(this._mc);
    }

    private prepareWinCap(callBackFn: () => void): void {
        this._buttonCallBack = callBackFn;

        PearlHarbor.keypadPlugin.hide();
        this.visible = true;
        this._countUp.preCalculateScale(0, PearlHarbor.model.gameData.accumulatedRoundWin);
    }

    private hideWinCapView(): void {
        this.visible = false;
    }

    private resetSpine(): void {
        this._spine.updateTransform();
        this._spine.visible = false;
        this._spine.alpha = 0;
        this._spine.skeleton.setToSetupPose();
        this._spine.skeleton.setSlotsToSetupPose();
        this._spine.state.setEmptyAnimation(0, 0);
        this._spine.state.clearTracks();
    }

    private createContinueBtn(): void {
        this._button = new ContinueButton();
        this.hideContinueBtn();
        this._button.pivot.set(this._button.width / 2, this._button.height / 2);
        this._button.addClickCallback(() => this.onButtonClick());

        this.addChild(this._button);
    }

    private onButtonClick(): void {
        PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.CONTINUE_BUTTON);
        this._countUp.visible = false;
        this._mc.visible = false;
        PearlHarbor.kamakazi.hide();
        PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.CAP_WIN_END);
        PearlHarborSoundManager.stopSoundEffect(PearlHarborSoundConfig.CAP_WIN_MUSIC);
        PearlHarborSoundManager.fadeMusic(1, 1953, true);
        PearlHarbor.keypadPlugin.show();
        this.hideContinueBtn();
        this.hideWinCapView();
        this._buttonCallBack();
        this._trackCount = 0;
    }

    private showContinueBtn(): void {
        this._button.visible = true;
        this._button.enable(true);
    }

    private hideContinueBtn(): void {
        this._button.visible = false;
        this._button.enable(false);
    }

    private createCountUp(): void {
        this._countUp = new BasicBitmapCountUp({
            fontName: PearlHarborGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 160,
            tint: 0xffffff
        }, 720, new PIXI.Point(0.5));
        this._countUp.visible = false;
        this._countUp.position.set(360, 360);

        this.addChild(this._countUp);
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        this._overlay.clear();
        this._overlay.beginFill(this._overlayColor);
        this._overlay.drawRect(bounds.left, bounds.top, bounds.width, bounds.height);

        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom);
        }
    }

    public playWinCapAnimations(callBackFn: () => void): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this.onResize();

        tl.add(() => {
            this.resetSpine();
            this.prepareWinCap(callBackFn);
            PearlHarbor.keypadPlugin.setWin(PearlHarbor.model.gameData.accumulatedRoundWin, true);
            PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.CAP_WIN_START);
            PearlHarborSoundManager.fadeMusic(0, 1952, true);
        });
        tl.add(gsap.fromTo(this._overlay, {alpha: 0}, {alpha: 1, duration: 1.951}));
        tl.add([
            () => {
                this._spine.visible = true;
                this._spine.state.setAnimation(0, "SinglePlane", true);
                this._spine.state.setAnimation(1, "allPlane", true);
                this._spine.state.setAnimation(2, "boats", true);
                this._countUp.visible = true;
                this._countUp.alpha = 1;
            },
            gsap.to(this._spine, {duration: 0.2, alpha: 1}),
            this._countUp.getCountAnimation(0, PearlHarbor.model.gameData.accumulatedRoundWin, 15.610)
        ]);
        tl.add(() => {
            this._mc.visible = true;
            this.showContinueBtn();
            PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.CAP_WIN_MUSIC, 1000);
        });
        return tl;
    }
}